import { IColor, IWidgetCustomProperty, OneOfCustomPropertyDto } from '@studio/domain';
import { FeedDto, WidgetFontStyleDto, WidgetImageDto } from '@studio/domain/api/sapi';
import { cloneDeep } from '@studio/utils/clone';
import { isUrl } from '@studio/utils/url';
import { clamp } from '@studio/utils/utils';
import { deserializePropertyValue, serializePropertyValue } from './property-value-serializer';

export function convertWidgetValueToDto(
    property: IWidgetCustomProperty
): OneOfCustomPropertyDto['value'] {
    // TODO: Clean up value value being string all the time.

    const { unit, value } = property;

    switch (unit) {
        case 'text':
            return value as string;
        case 'number':
            return clamp(value as number, -9999999, 9999999);
        case 'boolean':
            if (typeof value === 'string') {
                return value === 'true' ? true : false;
            }
            return value as boolean;
        case 'select':
            const selectValue =
                typeof window !== 'undefined'
                    ? JSON.parse(atob(value as string))
                    : JSON.parse(Buffer.from(value as string, 'base64').toString());
            return selectValue;
        case 'color':
            return (value as IColor).toString();
        case 'image': {
            const splits = (value as string).split(' '); // TODO: clean this up. It shouldn't be a string
            const imageDto = { id: splits[0], src: splits[1] };
            return imageDto as WidgetImageDto;
        }
        case 'font': {
            const splits = (value as string).split(' '); // TODO: clean this up. It shouldn't be a string

            let src = splits[3];

            try {
                // Support old data
                if (!isUrl(src)) {
                    if (typeof window !== 'undefined') {
                        src = atob(splits[3]);
                    } else {
                        src = Buffer.from(splits[3], 'base64').toString();
                    }
                }
            } catch {
                // eslint-disable-next-line no-empty
            }

            const style = splits[2] as WidgetFontStyleDto['style'];

            const widgetFontStyleDto: WidgetFontStyleDto = {
                id: splits[0],
                weight: parseInt(splits[1], 10) as 100,
                style,
                src,
                fontFamilyId: splits[4]
            };
            return widgetFontStyleDto;
        }
        case 'feed': {
            const feedValue = deserializePropertyValue('feed', value) as FeedDto;
            if (feedValue.type === undefined) {
                feedValue.type = 'text';
            }
            return feedValue;
        }
        default:
            throw new Error(`Unrecognized property '${property.name}'.`);
    }
}

export function convertWidgetPropertyToDto(property: IWidgetCustomProperty): OneOfCustomPropertyDto {
    property = cloneDeep(property);
    let value: OneOfCustomPropertyDto['value'];
    if (!property.versionPropertyId) {
        value = convertWidgetValueToDto(property);
    }

    return {
        label: property.label,
        name: property.name,
        value,
        versionPropertyId: property.versionPropertyId,
        unit: property.unit
    } as OneOfCustomPropertyDto;
}

export function deserializeWidgetValue(property: OneOfCustomPropertyDto): string {
    const unit = property.unit;
    const value = property.value;

    // Serialization flow is messy, if it's a string the value is already serialized
    if (unit && typeof value === 'string') {
        return value;
    }

    switch (unit) {
        case 'text':
        case 'number':
        case 'color':
        case 'boolean':
            return value as string;
        case 'select':
            return typeof window !== 'undefined'
                ? btoa(JSON.stringify(value))
                : Buffer.from(JSON.stringify(value)).toString('base64');
        case 'image': {
            const { id, src } = value as WidgetImageDto;
            return `${id} ${src}`;
        }
        case 'font': {
            const font = value as WidgetFontStyleDto;
            let src = font.src;
            if (src && !src.includes('r=') && !src.includes('%3D')) {
                src += `${src.includes('?') ? '&' : '?'}r=${Math.random()}`; // Add random string to fonts URLs for uniqueness when replacing in creative-downloader when exporting
            }
            return `${font.id} ${font.weight} ${font.style} ${src} ${font.fontFamilyId}`;
        }
        case 'feed': {
            return serializePropertyValue('feed', property.value) as string;
        }
        default:
            throw new Error(`Unrecognized value '${(property as any).type}'.`);
    }
}

export function deserializeWidgetProperty(
    customPropertyDto: OneOfCustomPropertyDto
): IWidgetCustomProperty {
    return {
        label: customPropertyDto.label,
        name: customPropertyDto.name,
        unit: customPropertyDto.unit,
        value: deserializeWidgetValue(customPropertyDto),
        versionPropertyId: customPropertyDto.versionPropertyId
    };
}
